import { PurchaseOrder } from "../services/purchaseOrderService";

export type RetailerKpis = {
  retailerId: string;
  orderCount: number;
  totalAmount: number;
  totalUnits: number;
  avgOrderValue: number;
  retrocessionAmount: number;
  importCost: number;
  netRevenue: number;
  onTimeRate: number;
  deliveredCount: number;
  inDeliveryCount: number;
  lastOrderDate?: Date;
};

export type GlobalRetailerKpis = {
  orderCount: number;
  totalAmount: number;
  totalUnits: number;
  avgOrderValue: number;
  retrocessionAmount: number;
  importCost: number;
  netRevenue: number;
  onTimeRate: number;
  deliveredCount: number;
  inDeliveryCount: number;
  activeRetailers: number;
};

const DELIVERY_STATUSES = new Set([
  "received",
  "completed",
  "noted_delivered",
]);

const IN_DELIVERY_STATUSES = new Set([
  "sent",
  "in_delivery",
  "partially_received",
  "noted_delivered",
]);

const getOrderDate = (po: PurchaseOrder): Date => {
  const raw = po.creationDate || po.orderDate || po.updatedAt || po.lastUpdateDate;
  return raw ? new Date(raw) : new Date(0);
};

const getTargetDeliveryDate = (po: PurchaseOrder): Date | null => {
  const raw =
    po.expectedDeliveryDate || po.estimatedDeliveryAt || po.deliveryDate || null;
  return raw ? new Date(raw) : null;
};

const getDeliveredAt = (po: PurchaseOrder): Date | null => {
  const raw = po.deliveredAt || po.lastTrackingAt || null;
  return raw ? new Date(raw) : null;
};

const getImportCost = (po: PurchaseOrder): number => {
  return Number(po.importCost ?? po.estimatedCost ?? po.shippingCost ?? 0) || 0;
};

const getOrderUnits = (po: PurchaseOrder): number => {
  return (po.items || []).reduce(
    (sum, item) => sum + (Number(item.quantity) || 0),
    0
  );
};

const isDelivered = (po: PurchaseOrder): boolean => {
  return Boolean(getDeliveredAt(po)) || DELIVERY_STATUSES.has(po.status || "");
};

const isInDelivery = (po: PurchaseOrder): boolean => {
  return IN_DELIVERY_STATUSES.has(po.status || "");
};

const computeOnTimeRate = (orders: PurchaseOrder[]): {
  rate: number;
  deliveredCount: number;
} => {
  let delivered = 0;
  let onTime = 0;

  orders.forEach((po) => {
    if (!isDelivered(po)) return;
    const deliveredAt = getDeliveredAt(po);
    const target = getTargetDeliveryDate(po);
    if (!deliveredAt || !target) return;
    delivered += 1;
    if (deliveredAt.getTime() <= target.getTime()) {
      onTime += 1;
    }
  });

  return {
    rate: delivered ? (onTime / delivered) * 100 : 0,
    deliveredCount: delivered,
  };
};

export const buildRetailerKpis = (
  purchaseOrders: PurchaseOrder[],
  retailerId: string
): RetailerKpis => {
  const orders = purchaseOrders.filter(
    (po) => String(po.retailerId || "") === String(retailerId)
  );

  const orderCount = orders.length;
  const totalAmount = orders.reduce(
    (sum, po) => sum + (Number(po.totalAmount) || 0),
    0
  );
  const totalUnits = orders.reduce((sum, po) => sum + getOrderUnits(po), 0);
  const retrocessionAmount = orders.reduce(
    (sum, po) => sum + (Number(po.retrocessionAmount) || 0),
    0
  );
  const importCost = orders.reduce((sum, po) => sum + getImportCost(po), 0);
  const netRevenue = totalAmount - importCost - retrocessionAmount;
  const { rate, deliveredCount } = computeOnTimeRate(orders);
  const inDeliveryCount = orders.filter(isInDelivery).length;

  const lastOrderDate = orders.length
    ? orders
        .map(getOrderDate)
        .sort((a, b) => b.getTime() - a.getTime())[0]
    : undefined;

  return {
    retailerId,
    orderCount,
    totalAmount,
    totalUnits,
    avgOrderValue: orderCount ? totalAmount / orderCount : 0,
    retrocessionAmount,
    importCost,
    netRevenue,
    onTimeRate: rate,
    deliveredCount,
    inDeliveryCount,
    lastOrderDate,
  };
};

export const buildRetailerKpisMap = (
  purchaseOrders: PurchaseOrder[]
): Map<string, RetailerKpis> => {
  const retailerIds = Array.from(
    new Set(purchaseOrders.map((po) => String(po.retailerId || "")).filter(Boolean))
  );

  const map = new Map<string, RetailerKpis>();
  retailerIds.forEach((id) => {
    map.set(id, buildRetailerKpis(purchaseOrders, id));
  });
  return map;
};

export const buildGlobalRetailerKpis = (
  purchaseOrders: PurchaseOrder[]
): GlobalRetailerKpis => {
  const orderCount = purchaseOrders.length;
  const totalAmount = purchaseOrders.reduce(
    (sum, po) => sum + (Number(po.totalAmount) || 0),
    0
  );
  const totalUnits = purchaseOrders.reduce(
    (sum, po) => sum + getOrderUnits(po),
    0
  );
  const retrocessionAmount = purchaseOrders.reduce(
    (sum, po) => sum + (Number(po.retrocessionAmount) || 0),
    0
  );
  const importCost = purchaseOrders.reduce(
    (sum, po) => sum + getImportCost(po),
    0
  );
  const netRevenue = totalAmount - importCost - retrocessionAmount;
  const { rate, deliveredCount } = computeOnTimeRate(purchaseOrders);
  const inDeliveryCount = purchaseOrders.filter(isInDelivery).length;
  const activeRetailers = new Set(
    purchaseOrders.map((po) => String(po.retailerId || "")).filter(Boolean)
  ).size;

  return {
    orderCount,
    totalAmount,
    totalUnits,
    avgOrderValue: orderCount ? totalAmount / orderCount : 0,
    retrocessionAmount,
    importCost,
    netRevenue,
    onTimeRate: rate,
    deliveredCount,
    inDeliveryCount,
    activeRetailers,
  };
};
